import { buildNewContractDraft } from "./contract-editor";

export const DISCARD_LOAN_CHANGES_MESSAGE =
  "Há alterações não salvas neste contrato. Deseja descartá-las e continuar?";

const LOAN_KEYS = [
  "number",
  "category",
  "contracted_value",
  "installments",
  "installment_value",
  "contract_expenses_value",
  "installment_part_not_questioned_value",
  "installment_questioned_value",
  "interest_rate_monthly_value",
  "interest_rate_yearly_value",
  "contract_at",
  "first_installment_at",
  "grace_period_days",
  "installments_payed",
  "is_renegotiation",
  "renegotiation_installments",
  "renegotiation_value",
  "interest_rate_average_monthly_value",
  "interest_rate_average_yearly_value",
  "updated_at",
  "total_amortization",
  "total_installment_value",
  "total_overpaid_value",
  "total_legal_interest",
  "total_correction_value",
];

const PRINCIPAL_KEYS = [
  ...LOAN_KEYS,
  "date",
  "status",
  "service_obs",
  "process_protocol_obs",
  "filed_at",
  "file_at",
  "state",
  "process_number",
  "complaint_protocol_number",
  "has_ajg",
  "has_after_sales_video",
  "declares_irpf",
  "customer",
  "bank",
  "lawyer",
  "lawyer_wallet",
  "sales_consultant",
  "responsible_user",
];

const entityId = (value) => {
  if (value && typeof value === "object") return value.id ?? null;
  return value ?? null;
};

export function loanSnapshot(contract, installments = []) {
  const data = {};
  LOAN_KEYS.forEach((key) => {
    data[key] = contract?.[key] ?? null;
  });
  data.contract_type_id = entityId(contract?.contract_type);
  data.installments_rows = (installments || []).map((row) => ({
    installment_number: row.installment_number ?? null,
    installment_value: row.installment_value ?? null,
  }));
  return JSON.stringify(data);
}

export function principalSnapshot(contract) {
  if (!contract) return "";
  const data = {};
  PRINCIPAL_KEYS.forEach((key) => {
    const value = contract[key];
    data[key] = value && typeof value === "object" ? entityId(value) : value ?? null;
  });
  data.contract_type_id = entityId(contract.contract_type);
  return JSON.stringify(data);
}

export function shouldUseLegacyValuesLayout(processContract, selection) {
  const children = processContract?.child_contracts || [];
  if (children.length > 0) return false;
  const kind = selection?.kind;
  if (kind === "new" || kind === "child") return false;
  return true;
}

export function shouldShowCalculationIdentity(_processContract, selection) {
  return selection?.kind === "child" || selection?.kind === "new";
}

export function childTabLabel(child, position) {
  const number = String(child?.number || "").trim();
  if (child?.id && number) return `Contrato ${number}`;
  return `Contrato ${position}`;
}

export function buildContractTabs(processContract, draft) {
  const children = Array.isArray(processContract?.child_contracts) ? processContract.child_contracts : [];
  const tabs = children.map((child, index) => ({
    value: `loan:${child.id}`,
    label: childTabLabel(child, index + 2),
    childId: child.id,
  }));
  if (draft && !draft.id) {
    tabs.push({
      value: "loan:new",
      label: childTabLabel(draft, children.length + 2),
      childId: null,
    });
  }
  return tabs;
}

export function loanActionPermissions(permissions, options = {}) {
  const blocked = Boolean(options.isFileOnlyMode || options.bulkMode);
  const canEdit = Boolean(permissions?.can_edit_contracts) && !blocked;
  const canCreate = Boolean(permissions?.can_create_contracts) && !blocked;
  const canDelete = Boolean(permissions?.can_delete_contracts) && !blocked && !options.isPrincipal;
  return { canView: true, canEdit, canCreate, canDelete };
}

export function canLeaveLoanEditor(isDirty, confirm) {
  if (!isDirty) return true;
  return Boolean(confirm(DISCARD_LOAN_CHANGES_MESSAGE));
}

export function resolveCalculationTarget(selection, principal, principalInstallments, child, childInstallments) {
  if (selection?.kind === "child" || selection?.kind === "new") {
    if (!child) return { ready: false, contract: null, installments: [], ownerId: null };
    return {
      ready: true,
      contract: child,
      installments: childInstallments || [],
      ownerId: child.id ?? "new",
    };
  }
  if (selection?.kind === "collapsed") {
    return { ready: false, contract: null, installments: [], ownerId: null };
  }
  return {
    ready: true,
    contract: principal,
    installments: principalInstallments || [],
    ownerId: principal?.id ?? "principal",
  };
}

export function resolveEditorTarget(contract) {
  if (contract?.parent_contract) {
    return { processId: contract.parent_contract, childId: contract.id };
  }
  return { processId: contract?.id ?? null, childId: null };
}

export function buildChildDraft(parent) {
  const draft = buildNewContractDraft(parent?.date || null, parent?.customer || null);
  return {
    ...draft,
    parent_contract: parent?.id ?? null,
    customer: parent?.customer ?? null,
    bank: parent?.bank ?? null,
    date: parent?.date ?? draft.date,
    status: parent?.status ?? draft.status,
    service_obs: parent?.service_obs || "",
    process_protocol_obs: parent?.process_protocol_obs || "",
    filed_at: parent?.filed_at ?? null,
    file_at: parent?.file_at ?? null,
    state: parent?.state || "",
    process_number: parent?.process_number || "",
    complaint_protocol_number: parent?.complaint_protocol_number || "",
    has_ajg: Boolean(parent?.has_ajg),
    has_after_sales_video: Boolean(parent?.has_after_sales_video),
    declares_irpf: Boolean(parent?.declares_irpf),
    lawyer: parent?.lawyer ?? null,
    lawyer_wallet: parent?.lawyer_wallet ?? null,
    sales_consultant: parent?.sales_consultant ?? null,
    responsible_user: parent?.responsible_user ?? null,
    number: "",
    category: "NORMAL",
    contract_installments: [],
  };
}

const CHILD_PAYLOAD_OMIT = [
  "child_contracts",
  "restitution_summary",
  "customer",
  "bank",
  "lawyer",
  "lawyer_wallet",
  "sales_consultant",
  "responsible_user",
  "contract_type",
  "calculation_pending",
  "restitution_applicable",
  "restitution_value",
  "is_rate_below_abusive_threshold",
  "rate_excess_percent",
  "_originalStatus",
  "created_by",
  "deleted_by",
  "is_deleted",
];

export function buildChildSavePayload(child, installments, parent) {
  const payload = { ...child, contract_installments: installments || [] };
  CHILD_PAYLOAD_OMIT.forEach((key) => {
    delete payload[key];
  });
  payload.parent_contract = parent.id;
  payload.customer_id = entityId(parent.customer);
  payload.bank_id = entityId(parent.bank);
  payload.contract_type_id = entityId(child.contract_type) || child.contract_type_id || null;
  payload.lawyer_id = entityId(parent.lawyer);
  payload.lawyer_wallet_id = entityId(parent.lawyer_wallet);
  payload.sales_consultant_id = entityId(parent.sales_consultant);
  payload.responsible_user_id = entityId(parent.responsible_user);
  return payload;
}

export function mergePrincipalAfterRelatedSave(local, remote, baseline) {
  const dirty = principalSnapshot(local) !== baseline;
  if (!dirty) {
    return { ...remote, _originalStatus: local?._originalStatus };
  }
  const preserved = { ...local };
  delete preserved.child_contracts;
  delete preserved.restitution_summary;
  return {
    ...remote,
    ...preserved,
    id: remote.id,
    child_contracts: remote.child_contracts || [],
    restitution_summary: remote.restitution_summary,
    _originalStatus: local?._originalStatus,
  };
}

export function formatContractMoney(value) {
  if (value == null || value === "") return "—";
  const number = Number(value);
  if (Number.isNaN(number)) return "—";
  return number.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function loanCalculationLabel(summaryItem) {
  if (!summaryItem || summaryItem.calculation_pending) return "Cálculo pendente";
  if (summaryItem.restitution_applicable && summaryItem.restitution_value != null) {
    return `A restituir ${formatContractMoney(summaryItem.restitution_value)}`;
  }
  return "Calculado, sem valor a restituir";
}

export function restitutionBanner(summary, options = {}) {
  if (!summary) return null;
  const contracts = summary.contracts || [];
  const pendingNumbers = contracts
    .filter((item) => item.calculation_pending)
    .map((item) => item.number || "sem número");
  const pending = Boolean(summary.has_pending_calculations);
  const notices = [
    "Total indicativo pela regra exibida na tela. A divergência com o HTML ainda não foi validada, então este valor não é definitivo.",
  ];
  if (pending) {
    notices.unshift(
      "Resultado parcial: há contratos com cálculo pendente. Contrato sem cálculo não entra como zero."
    );
  }
  if (options.draftOpen) {
    notices.push("O contrato que está sendo preenchido ainda não entra neste resumo.");
  }
  return {
    contractCount: summary.contract_count,
    totalKnown: summary.total_restitution,
    minimum: summary.minimum_restitution_value,
    amountMissing: summary.amount_missing,
    minimumReached: Boolean(summary.minimum_reached) && !pending,
    pendingCount: pendingNumbers.length,
    pendingNumbers,
    partial: true,
    notices,
  };
}
